/**
 * Copyright Fitzba Technologies Inc. 2023
 * 
 * FilterSideCategory defines a component for filtering by category in the sidebar on the search results page.
 * The categories are displayed in a tree structure.
 * 
 * @author Yang Ming
 * @version January 15th, 2024
 */

import React, { useEffect, useState, useMemo } from 'react'
// import { AiOutlineDown, AiOutlineUp } from 'react-icons/ai'
import { useSelector } from 'react-redux'
import { Tree } from 'antd'
import {
  setFilterCategoryList,
  setFilterExpandedCategories,
  setCategoryList,
} from '@reducers/contentReducer'
import { getGlobalCategoryList } from '@services/api.globalCategory.service'
import FilterSideSearchBar from './FilterSideSearchBar'
import FilterSideSectionHeader from './FilterSideSectionHeader'
import { setFilterState } from '@reducers/userReducer'

const getParentKey = (key, tree) => {
  let parentKey
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.children) {
      if (node.children.some((item) => item.key === key)) {
        parentKey = node.key
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children)
      }
    }
  }
  return parentKey
}

const dataList = []

/**
 * A component for filtering by category in a sidebar.
 * The categories are displayed in a tree structure.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.itemsFilter - The current filter settings.
 * @param {Function} props.dispatch - The Redux dispatch function.
 * @param {boolean} props.isMobile - A flag indicating whether the device is mobile.
 */
export default function FilterSideCategory({ itemsFilter, dispatch, isMobile }) {

  const generateList = (data) => {
    for (let i = 0; i < data.length; i++) {
      const node = data[i]
      const { key } = node
      dataList.push({
        key,
        title: node.title,
      })
      if (node.children) {
        generateList(node.children)
      }
    }
  }

  const isOpen = useSelector(state => state.user.filterState.category)
  const setIsOpen = (value) => {
    dispatch(setFilterState({ type: 'category', value }))
  }
  const isReloadCategories = useSelector(state => state.content.isReloadCategories)
  const categoryList = useSelector(state => state.content.categoryList)
  const categoryListExisted = useSelector(state => state.content.categoryListExisted)
  const categoryListExpanded = useSelector(state => state.content.categoryListExpanded)
  // const [isOpen, setIsOpen] = useState(false)
  // const [input, setInput] = useState('')
  const [expandedKeys, setExpandedKeys] = useState(categoryListExpanded)
  const [searchValue, setSearchValue] = useState('')
  const [autoExpandParent, setAutoExpandParent] = useState(true)
  const [allCheckbox, setAllCheckbox] = useState(false)

  generateList(categoryList)
  // console.log('dataList', dataList)
  // console.log('categoryList', categoryList)

  useEffect(() => {
    const getGlobalCategory = async () => {
      if (!isReloadCategories)
        return
      try {
        console.log('getGlobalCategoryList3333 is running')
        const res = await getGlobalCategoryList({
          // categories: itemsFilter?.stores.length === 1 ? categoryListExisted : [],
          categories: categoryListExisted,
          stores: itemsFilter?.stores,
          onlyOneStore: itemsFilter?.onlyOneStore,
        })
        dispatch(setCategoryList(res.data))
      } catch (error) {
        console.log('getGlobalCategoryList3333', error)
      }
    }
    getGlobalCategory()
    // }, [dispatch, categoryListExisted, itemsFilter?.stores, itemsFilter?.onlyOneStore])
  }, [dispatch, isReloadCategories, categoryListExisted, itemsFilter?.onlyOneStore, itemsFilter?.stores])

  // const onExpand = expandedKeysValue => {
  //   // console.log('onExpand', expandedKeysValue)
  //   // setExpandedKeys(expandedKeysValue)
  //   dispatch(setFilterExpandedCategories(expandedKeysValue))
  //   // setAutoExpandParent(false)
  // }

  const onExpand = (newExpandedKeys) => {
    setExpandedKeys(newExpandedKeys)
    setAutoExpandParent(false)
    dispatch(setFilterExpandedCategories(newExpandedKeys))
  }

  const onCheck = (checkedKeysValue, e) => {
    // console.log('onCheck', checkedKeysValue)
    // setCheckedKeys(checkedKeysValue)
    dispatch(setFilterCategoryList(checkedKeysValue))
  }

  const onChange = (e) => {
    const { value } = e.target
    console.log('onChange', value)
    // console.log('categoryList', categoryList)
    const newExpandedKeys = dataList
      .map((item) => {
        if (item.title?.toLowerCase().indexOf(value?.toLowerCase()) > -1) {
          return getParentKey(item.key, categoryList)
        }
        return null
      })
      .filter((item, i, self) => !!(item && self.indexOf(item) === i))
    setExpandedKeys(newExpandedKeys)
    setSearchValue(value)
    setAutoExpandParent(true)
  }

  function defaultSetting() {
    setSearchValue('')
    setExpandedKeys([])
    setAutoExpandParent(true)
  }

  const treeData = useMemo(() => {
    if (!searchValue) {
      defaultSetting()
      return categoryList
    }
    let capitalizedString = searchValue?.charAt(0).toUpperCase() + searchValue?.slice(1)
    const loop = (data) =>
      data.map((item) => {
        const strTitle = item.title
        const index = strTitle?.indexOf(capitalizedString)
        const beforeStr = strTitle.substring(0, index)
        const afterStr = strTitle.slice(index + searchValue.length)
        const title =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className="site-tree-search-value">{capitalizedString}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          )
        if (item.children) {
          return {
            title,
            key: item.key,
            children: loop(item.children),
          }
        }
        return {
          title,
          key: item.key,
        }
      })
    return loop(categoryList)
  }, [searchValue, categoryList])

  function onClickAllCheckbox(checked) {
    console.log('onClickAllCheckbox', checked)
    if (checked) {
      // select all
      // const ids = dataList?.map(item => item.key)
      dispatch(setFilterCategoryList(['All Categories']))
    } else {
      // deselect all
      // const ids = filterList?.stores.map(item => item.id)
      dispatch(setFilterCategoryList([]))
    }
    setAllCheckbox(checked)
  }

  let checkedList = itemsFilter?.categories
  if (itemsFilter?.categories.length === 1 && itemsFilter?.categories[0] === 'All Categories')
    checkedList = dataList?.map(item => item.key)

  return (
    <div className='filter-category'>
      <FilterSideSectionHeader
        title='Category'
        isOpen={isOpen}
        onClickTitle={() => setIsOpen(!isOpen)}
        isHighlight={itemsFilter?.categories?.length > 0}
      />
      <FilterSideSearchBar
        input={searchValue}
        isOpen={isOpen}
        onSearch={onChange}
        onClear={defaultSetting}
        allCheckbox={allCheckbox}
        onSelectAll={() => onClickAllCheckbox(!allCheckbox)}
        selectText={['Select All', 'Clear All']}
        isShowClear={true}
        onClearAll={() => onClickAllCheckbox(false)}
      />
      <Tree
        style={{ display: isOpen ? 'flex' : 'none', marginTop: 10, left: '-15px' }}
        // selectable={false}
        className='filter-category-tree'
        checkable
        // treeData={categoryList}
        treeData={treeData}
        onExpand={onExpand}
        expandedKeys={expandedKeys}
        autoExpandParent={autoExpandParent}
        onCheck={onCheck}
        checkedKeys={checkedList}
      />
    </div>
  )
}
